import status from '@/utils/status'

const HALF_SPACE = "<small style='font-size: 0.5em;'>&nbsp;</small>"
/** 기준명용 반띄움 — 이미 적용됐는지 판별해야 해서 클래스로 표식을 남긴다 */
const SI_HALF_SPACE = "<small class='si-space' style='font-size: 0.5em;'>&nbsp;</small>"
const SI_SPACE_MARKER = "class='si-space'"

const groupDigits = (str, spacing) => {
	if (str == null || str === '') return ''
	const parts = str.split('.')
	parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, spacing)
	if (parts[1]) {
		parts[1] = parts[1].replace(/(\d{3})(?=\d)/g, '$1' + spacing)
	}
	return parts.join('.')
}

/** 숫자 천단위 간격만 적용(HTML 각주 등은 제외) */
const applyHalfSpacingToPlainString = (str) => groupDigits(str, HALF_SPACE)

// 앞에서부터 매칭되므로 긴 단위를 먼저 둔다.
// mm·mL·mg 가 m 보다 뒤에 오면 "2000mm" 이 "2000m" + "m" 으로 잘린다.
const SI_UNITS = [
	'㎡', '㎥', '㎠', '㎤', '㎣', '㎦', 'm²', 'm³',
	'mL', '㎖', '㎕', 'μL', 'uL', 'ℓ', 'L',
	'㎎', 'mg', '㎍', 'μg', 'ug', 'ng', '㎏', 'kg', 'g',
	'ppm', 'ppb', 'ppt', '℃', '㎈',
	'ha', 'km', 'cm', 'mm', 'nm', 'm2', 'm3', 'm',
	't', '톤'
]

// 기준명에는 단위가 아닌 숫자가 더 많다 — "[별표 9]", "고시 제2020-2호", 연도 등.
// 그런 숫자까지 천단위로 끊으면 1997 이 "1 997" 이 되므로 단위가 붙은 것만 잡는다.
// 뒤에 알파벳이 이어지면 단위가 아니다("2000mol" 의 m).
const SI_NUMBER_UNIT = new RegExp(
	`(\\d[\\d,]*(?:\\.\\d+)?)\\s*(${SI_UNITS.join('|')})(?![A-Za-z])`,
	'g'
)

const format = {
	date: (data) => {
		/* === 방지코드: 비-날짜 문자열은 원문 그대로 반환 === */
		try {
			if (typeof data === 'string') {
				const s = data.trim();
				if (!s) return '';
				// 날짜처럼 보이는 패턴인지 간단 판별
				const looksLikeDate =
					/^\d{8}$/.test(s) ||                       // 20250916
					/^\d{4}-\d{1,2}-\d{1,2}$/.test(s) ||       // 2025-09-16 / 2025-9-6
					/^\d{4}[/.]\d{1,2}[/.]\d{1,2}$/.test(s);   // 2025/09/16, 2025.9.6
				if (!looksLikeDate) {
					// 날짜가 아니면 그대로 돌려준다
					return s;
				}
				// 숫자 8자리면 하이픈 삽입(기존 로직이 new Date로 처리하기 좋게)
				if (/^\d{8}$/.test(s)) {
					data = `${s.slice(0,4)}-${s.slice(4,6)}-${s.slice(6,8)}`;
				}
			} else if (data && typeof data === 'object' && !(data instanceof Date)) {
				// 객체 형태라면 date 필드만 간단 정규화(숫자8자리 → 하이픈)
				if (typeof data.date === 'string' && /^\d{8}$/.test(data.date)) {
					data = { ...data, date: `${data.date.slice(0,4)}-${data.date.slice(4,6)}-${data.date.slice(6,8)}` };
				}
			}
		} catch (e) { /* 무시하고 기존 로직으로 진행 */ }
		/* === 방지코드 끝 === */

		let result = data;
		// 객체 입력은 출력 옵션의 참/거짓과 무관하게 date 필드에서 읽는다.
		if (data && typeof data === 'object' && !(data instanceof Date)) {
			result = data.date
		}
		if (!result || (typeof result === 'string' && !result.trim())) {
			return ''
		}
		let current = new Date(result);

		/* === 방지코드: Invalid Date면 문자열은 원문 반환, 그 외엔 '' === */
		if (isNaN(current.getTime())) {
			return (typeof result === 'string') ? result : '';
		}
		/* === 방지코드 끝 === */

		let year = current.getFullYear();
		if (!year) {
			return data;
		}
		let month = current.getMonth() < 9 ? `0${current.getMonth() + 1}` : current.getMonth() + 1;
		let day = current.getDate() < 10 ? `0${current.getDate()}` : current.getDate();
		let hour = current.getHours() < 10 ? `0${current.getHours()}` : current.getHours();
		let minutes = current.getMinutes() < 10 ? `0${current.getMinutes()}` : current.getMinutes();
		let dateFormat = `${year}-${month}-${day}`;
		let datetimeFormat = `${dateFormat} ${hour}:${minutes}`
		if (dateFormat.indexOf('1899') >= 0) {
			return ''
		}

		return data?.onlyDate != null && data?.onlyDate == false ? datetimeFormat : dateFormat;
	},
	reportDate: (date) => {
		if(!date) return '-';
		const d = new Date(date);
		const year = d.getFullYear();
		const month = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${year}. ${month}. ${day}.`;
	},
	/** 수질 측정기록부(ReportWater) 발급일 텍스트 → certificate.document_date 파싱용 */
	parseWaterRecordDateText: (text) => {
		if (!text || typeof text !== 'string') return '';
		const trimmed = text.trim();
		const m = trimmed.match(/(\d{4})\s*년[\s\u00a0]*(\d{1,2})\s*월[\s\u00a0]*(\d{1,2})\s*일/);
		if (!m) return trimmed;
		const year = m[1];
		const month = String(m[2]).padStart(2, '0');
		const day = String(m[3]).padStart(2, '0');
		return `${year}. ${month}. ${day}.`;
	},
	/**
	 * 발행일자(표준 표기 `2026. 04. 13.`) → 수질 측정기록부 인쇄 표기 `2026 년    04 월    13 일`
	 * parseWaterRecordDateText 의 역함수. 표지와 기록부가 하나의 발행일자를 공유하기 위해 쓴다.
	 * 표준 표기로 못 읽으면 원문을 그대로 돌려준다(사용자가 자유 입력한 문구 보존).
	 */
	waterRecordDateText: (text) => {
		if (!text || typeof text !== 'string') return '';
		const m = text.trim().match(/(\d{4})\D+(\d{1,2})\D+(\d{1,2})/);
		if (!m) return text.trim();
		const month = String(m[2]).padStart(2, '0');
		const day = String(m[3]).padStart(2, '0');
		return `${m[1]} 년    ${month} 월    ${day} 일`;
	},
	/** 분석기간(수질 측정기록부 등): 26. 04. 15 ~ 26. 04. 17 */
	analysisPeriodDate: (date) => {
		if (!date) return '-';
		const d = new Date(date);
		if (isNaN(d.getTime())) return '-';
		const year = String(d.getFullYear()).slice(-2);
		const month = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${year}. ${month}. ${day}`;
	},
	/** 시험기간(시험성적서 요약): 2026. 04. 13. ~ 2026. 04. 15. */
	analysisPeriodDateCertSummary: (date) => {
		if (!date) return '-';
		const d = new Date(date);
		if (isNaN(d.getTime())) return '-';
		const year = String(d.getFullYear());
		const month = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${year}. ${month}. ${day}.`;
	},
	recordDate: (date) => {
		if(!date) return '-';
		const d = new Date(date);
		const year = String(d.getFullYear()).slice(-2);
		const month = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${year}. ${month}. ${day}.`;
	},
	makeHalfSpacing: (value) => {
		if (value == null || value === undefined || value === '') return '-'
		const s = value.toString()
		const supIdx = s.indexOf('<sup')
		if (supIdx >= 0) {
			const base = s.slice(0, supIdx).trimEnd()
			const tail = s.slice(supIdx)
			if (!base) return tail
			return `${applyHalfSpacingToPlainString(base)} ${tail}`
		}
		return applyHalfSpacingToPlainString(s)
	},
	/**
	 * 법정기준명 SI 표기 — 단위가 붙은 숫자에만 천단위·단위 간격을 넣는다.
	 * 예) "지하수 2000㎡ 이상" → "지하수 2 000 ㎡ 이상"
	 *
	 * 값 포맷터(makeHalfSpacing)와 달리 빈 값은 '-' 가 아니라 '' 이다.
	 * 기준 미지정이 정상 상태라 '-' 로 채우면 빈 기준명 자리에 작대기가 찍힌다.
	 */
	makeStandardNameSpacing: (value) => {
		if (value == null || value === '') return ''
		const s = value.toString()
		// 헤더 텍스트가 각주 목록으로 한 번 더 흘러가므로 이중 적용을 막는다
		if (s.includes(SI_SPACE_MARKER)) return s
		return s.replace(
			SI_NUMBER_UNIT,
			(_, num, unit) =>
				`${groupDigits(num.replace(/,/g, ''), SI_HALF_SPACE)}${SI_HALF_SPACE}${unit}`
		)
	},
	number: (val) => {
		let result = val;
		if(typeof result === 'string') {
			result = val.replaceAll(',', '');
			result = Number(result)
		}
		return result != null ? result.toLocaleString() : 0;
		// let result = Number(val);
		// return result > 0 ? result.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : result;
	},
	/**
	 * 음수를 입력할 수 있는 금액 입력칸 전용 포맷터.
	 *
	 * ── 왜 number 를 못 쓰나 (2026-08-20 현업 2-3) ────────────────────────
	 * fields.vue 의 amount 타입은 `:formatter` 로 **타이핑 한 글자마다** 값을 변환한다.
	 * format.number 는 문자열을 무조건 Number() 로 바꾸므로, 음수를 치려고 '-' 를
	 * 처음 누르는 순간 Number('-') → NaN → 'NaN' 이 되어 뒤에 숫자를 이어 칠 수 없다.
	 * 현업에는 "마이너스 금액이 입력되지 않는다"로 보였다(용역취소 등으로 다수 발생).
	 *
	 * 그래서 **입력 도중의 중간 상태('-', '', '-,')는 그대로 돌려주고**, 숫자로
	 * 해석될 때만 콤마를 찍는다. format.number 는 표·성적서·대시보드 등 전역에서
	 * 쓰이므로 건드리지 않고 별도 함수로 둔다.
	 */
	signedAmount: (val) => {
		if (val === null || val === undefined || val === '') return val
		const raw = String(val).replaceAll(',', '')
		// 입력 중간 상태 — 아직 숫자가 아니지만 지우면 안 된다
		if (raw === '-' || raw === '') return raw
		const n = Number(raw)
		// 숫자로 해석 안 되면 사용자가 친 값을 그대로 둔다 (NaN 으로 덮지 않는다)
		return Number.isNaN(n) ? val : n.toLocaleString()
	},
	placeholder: (word) => {
		if(word) {
			let lastLetter = word[word.length - 1];
			let uni = lastLetter.charCodeAt(0);
			return `${word}${(uni - 44032) % 28 != 0 ? '을' : '를'} 입력해주세요`;
		}
	},
	phone: (input) => {
		if(!input) {
			return ''
		}
		const cleanInput = input.replaceAll(/[^0-9]/g, "");
		let result = cleanInput;
		const length = cleanInput.length;
		if(length === 8) {
			result = cleanInput.replace(/(\d{4})(\d{4})/, '$1-$2');
		} else if(cleanInput.startsWith("02") && (length === 9 || length === 10)) {
			result = cleanInput.replace(/(\d{2})(\d{3,4})(\d{4})/, '$1-$2-$3');
		} else if(!cleanInput.startsWith("02") && (length === 10 || length === 11)) {
			result = cleanInput.replace(/(\d{3})(\d{3,4})(\d{4})/, '$1-$2-$3');
		}
		return result;
	},
	new: (item, date) => {
		let standard = new Date(date);
		if((today - standard)/(60*60*1000) <= 24) {
			item.isNew = true
		} else {
			item.isNew = false
		}
		return item
	},
	flat: (data, orgKey) => {
		const keyList = orgKey.split(".");
		for(const key of keyList) {
			data = data[key] != null ? data[key] : '';
		}

		return data;
	},
	setFlat: (data, orgKey, value) => {
		let keyList = orgKey.split('.');
		const lastKey = keyList[keyList.length - 1];
		keyList = keyList.slice(0, keyList.length - 1);

		for(const key of keyList) {
			data = data[key];
		}

		data[lastKey] = value
		return data
	},
	dataValue: (key, item, isSummary) => {
		if(!key) {
			return;
		}
		let value = format.flat(item, key)
		if(!value && value.length == 0) {
			return '-'
		}
		//입금 금액 부분만 제외
		if(isSummary && key === 'paidAmount') {
			return `${format.number(item.amount)}원 / <span>${format.number(item.paidAmount)}원</span>`
		} else if(isSummary && key === 'amount') {
			return `${format.number(item.amount - item.paidAmount)}원`
		}

		//상태
		if(key == 'status') {
			return status.text[value]
		//date format
		} else if (key.toLowerCase().indexOf('date') >= 0){
			return format.date({date: value, onlyDate: true})
		//phone
		} else if (key.toLowerCase().indexOf('phone') >= 0){
			return format.phone(value)
		} else if (key.toLowerCase().indexOf('fax') >= 0){
			return format.phone(value)
		//amount
		} else if (key.indexOf('mount') >= 0){
			return `${format.number(value)}원`
		} else {
			return value;
		}
	},
	highlighted: (text = '', highlight = '') => {
		if(!text) return;
		let highlightText = ''; 
		if(typeof highlight === 'object') {
			highlightText = highlight?.text;
		} else {
			highlightText = highlight;
		}
		if (!highlightText?.trim()) {
			return `<span>${text}</span>`
		}
		const escapeRegExp = (str = '') => (
			str.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1')
		)
		const regex = new RegExp(`(${escapeRegExp(highlightText)})`, 'gi')
   		const parts = text?.split(regex)

		let result = ''
		result += parts.filter(part => part).map((part, i) => {
			return regex.test(part) ? `<mark style="background-color: #FFE2E0;" key=${i}>${part}</mark>` : `<span key=${i}>${part}</span>`
		})
		result = result.replaceAll(',', '')
		return result;
	},
	regPw: (value) => {
		let result = true;
		var validatePassword = /^(?=.*[a-zA-z])(?=.*[0-9])(?=.*[$`~!@$!%*#^?&\\(\\)\-_=+]).{8,16}$/
		if(!validatePassword.test(value)) {
			result = false;
		}
		return result;
	},
	type: (key) => {
		if(key.indexOf('Date') >= 0) {
			
		}
	},
	checkBatchimEnding(word) {
		if (typeof word !== 'string') return null;
		
		var lastLetter = word[word.length - 1];
		var uni = lastLetter.charCodeAt(0);
		
		if (uni < 44032 || uni > 55203) return null;
		
		return (uni - 44032) % 28 != 0 ? '을' : '를';
	},
	lower(val) {
		return val ? val.toLowerCase() : val;
	},
	//숫자로 변환
	toNum(v) {
		const n = Number(v);
		return Number.isFinite(n) ? n : null;
	},
}

export default format
